import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGetClassesQuery, useDuplicateLessonMutation, useGenerateQuestionsMutation, useAssignClassMutation } from 'util/rtk-api'
import { RegenerateQuestionsButton, CopyLessonButton, BlueButton } from 'components/blue-button'
import DismissButton from 'components/dismiss-button'
import Dropdown from 'components/selector'
import { StudentTable } from 'components/tables/student-table'
import { DifficultyPills } from 'components/tables/difficulty-pills'
import { ClassContextProvider } from 'contexts/class-context'
import { LoadingModal } from 'components/loading-model'
import styles from 'css/components/lesson-builder/copy-to-class-overlay.module.css'
import listView from 'img/list-view.svg'
import { Class } from '@all-for-one/shared-types'
import { showToast } from 'components/toast'

interface CopyToClassOverlayProps {
    mode?: 'copy' | 'reassign'
    lessonId: string
    lessonName: string
    currentClassId: string
    currentGradeBand?: string
    currentGrades?: string[]
    onDismiss: () => void
}

// Helper function to derive grade band from class grades
const deriveGradeBand = (grades: string[]): string => {
    if (!grades || grades.length === 0) return '3-5'

    const gradeToBand = (grade: string): string => {
        const g = grade.trim().toUpperCase()
        if (g === 'K' || g === '1' || g === '2') return 'K-2'
        if (g === '3' || g === '4' || g === '5') return '3-5'
        if (g === '6' || g === '7' || g === '8') return '6-8'
        if (g === '9' || g === '10' || g === '11' || g === '12' || g === 'T') return '9-12'
        return '3-5'
    }

    const bands = grades.map(gradeToBand)
    const uniqueBands = Array.from(new Set(bands))
    const bandOrder = ['K-2', '3-5', '6-8', '9-12']
    const lowestBand = uniqueBands.sort((a, b) => bandOrder.indexOf(a) - bandOrder.indexOf(b))[0]

    return lowestBand
}

export const CopyToClassOverlay = ({
    mode = 'copy',
    lessonId,
    lessonName,
    currentClassId,
    currentGradeBand,
    currentGrades,
    onDismiss
}: CopyToClassOverlayProps) => {
    const navigate = useNavigate()
    const { data: classesData } = useGetClassesQuery(undefined)
    const [duplicateLesson, { isLoading: isDuplicating }] = useDuplicateLessonMutation()
    const [generateQuestions, { isLoading: isGenerating }] = useGenerateQuestionsMutation()
    const [assignClass, { isLoading: isAssigning }] = useAssignClassMutation()

    const isReassign = mode === 'reassign'

    const [selectedClass, setSelectedClass] = useState<Class | null>(null)
    const [newLessonName, setNewLessonName] = useState('')
    const [copySuccess, setCopySuccess] = useState<{ lessonId: string } | null>(null)
    const [reassignSuccess, setReassignSuccess] = useState(false)
    const [currentTableDifficulty, setCurrentTableDifficulty] = useState('All')
    const [isRegenerating, setIsRegenerating] = useState(false)

    const isCopying = isDuplicating || isGenerating
    const isProcessing = isCopying || isAssigning

    const classes = [...(classesData?.classes || [])].sort((a, b) => a.name.localeCompare(b.name))
    const availableClasses = classes.filter(c => c.classId !== currentClassId)

    // Derive current grade band from passed gradeBand or from grades array
    const derivedCurrentGradeBand = currentGradeBand || (currentGrades ? deriveGradeBand(currentGrades) : null)
    const selectedGradeBand = selectedClass ? deriveGradeBand(selectedClass.grades) : null
    const gradeBandsDiffer = selectedGradeBand && derivedCurrentGradeBand && selectedGradeBand !== derivedCurrentGradeBand

    const handleClassSelect = (className: string) => {
        const selected = availableClasses.find(c => c.name === className)
        setSelectedClass(selected || null)
        if (selected && !isReassign) {
            setNewLessonName(`${lessonName} (${selected.name})`)
        }
    }

    const handleCopy = async (regenerate: boolean) => {
        if (!selectedClass) return

        setIsRegenerating(regenerate)

        try {
            // Step 1: Duplicate the lesson
            const result = await duplicateLesson({
                lessonId,
                targetClassId: selectedClass.classId,
                newName: newLessonName || `${lessonName} (${selectedClass.name})`,
                regenerateQuestions: regenerate,
                targetGradeBand: regenerate ? selectedGradeBand : undefined
            }).unwrap()

            // Step 2: If regenerating, call generateQuestions with new grade band
            if (regenerate && selectedGradeBand) {
                await generateQuestions({
                    lessonId: result.lessonId,
                    gradeLevel: selectedGradeBand
                }).unwrap()
            }

            setCopySuccess({ lessonId: result.lessonId })
        } catch (error) {
            console.error('Error copying lesson:', error)
            showToast('Failed to copy lesson. Please try again.')
            setIsRegenerating(false)
        }
    }

    const handleReassign = async (regenerate: boolean) => {
        if (!selectedClass) return

        setIsRegenerating(regenerate)

        try {
            // Step 1: Reassign the lesson to the new class (removes from old class)
            await assignClass({
                lessonId,
                classId: selectedClass.classId,
                previousClassId: currentClassId,
                gradeBand: regenerate ? selectedGradeBand! : undefined
            }).unwrap()

            // Step 2: If regenerating, call generateQuestions on the same lesson
            if (regenerate && selectedGradeBand) {
                await generateQuestions({
                    lessonId,
                    gradeLevel: selectedGradeBand,
                    replaceExisting: true
                }).unwrap()
            }

            setReassignSuccess(true)
        } catch (error) {
            console.error('Error reassigning lesson:', error)
            showToast('Failed to reassign lesson. Please try again.')
            setIsRegenerating(false)
        }
    }

    const handleOpenNewLesson = () => {
        if (copySuccess) {
            onDismiss()
            navigate(`/lesson-builder?lessonId=${copySuccess.lessonId}`)
        }
    }

    // Show loading modal when processing
    if (isProcessing) {
        const loadingText = isReassign
            ? (isRegenerating ? 'Reassigning and regenerating questions...' : 'Reassigning lesson...')
            : (isRegenerating ? 'Copying lesson and regenerating questions...' : 'Copying lesson...')

        return (
            <LoadingModal>
                <div>{loadingText}</div>
                <div style={{ fontWeight: 500, marginTop: '0.5rem' }}>
                    {isReassign ? lessonName : (newLessonName || lessonName)}
                </div>
            </LoadingModal>
        )
    }

    // Success screen for copy mode
    if (copySuccess) {
        return (
            <div className="overlay">
                <div className={styles.successCard}>
                    <div className={styles.successMessage}>
                        Lesson copied successfully!
                    </div>
                    <div className={styles.successActions}>
                        <button
                            className={styles.secondaryButton}
                            onClick={onDismiss}
                        >
                            Stay Here
                        </button>
                        <BlueButton label="Open Lesson" onClick={handleOpenNewLesson} />
                    </div>
                </div>
            </div>
        )
    }

    // Success screen for reassign mode
    if (reassignSuccess) {
        return (
            <div className="overlay">
                <div className={styles.successCard}>
                    <div className={styles.successMessage}>
                        Lesson reassigned!
                    </div>
                    <div className={styles.successActions}>
                        <BlueButton label="Done" onClick={onDismiss} />
                    </div>
                </div>
            </div>
        )
    }

    const title = isReassign ? 'Reassign to Another Class' : 'Copy to Another Class'
    const handleAction = isReassign ? handleReassign : handleCopy

    return (
        <div className="overlay">
            <div className={`overlay-content-container ${styles.container}`}>
                <div className="overlay-content-header">
                    {title}
                    <DismissButton onClick={onDismiss} />
                </div>
                <div className={styles.content}>
                    {selectedClass ? (
                        <ClassContextProvider initialClass={selectedClass}>
                            {!isReassign && (
                                <div className={styles.field}>
                                    <label>New Lesson Name</label>
                                    <input
                                        type="text"
                                        value={newLessonName || lessonName}
                                        onChange={(e) => setNewLessonName(e.target.value)}
                                        placeholder={lessonName}
                                        className={styles.input}
                                    />
                                </div>
                            )}

                            <div className={styles.classSelectionRow}>
                                <div className={styles.field}>
                                    <label>Select Class</label>
                                    <Dropdown
                                        options={availableClasses.map(c => c.name)}
                                        onSelect={handleClassSelect}
                                        placeholder="Select a class..."
                                        style={styles.wideDropdown}
                                        value={selectedClass?.name}
                                    />
                                </div>
                                {gradeBandsDiffer && (
                                    <div className={styles.gradeBandWarning}>
                                        <span className={styles.warningIcon}>⚠️</span>
                                        <span className={styles.warningText}>
                                            <strong>Grade band mismatch:</strong> Questions were generated for grades {derivedCurrentGradeBand}, but this class is {selectedClass.grades.length === 1 ? 'grade' : 'grades'} {selectedClass.grades.join(', ')} ({selectedGradeBand} band).
                                        </span>
                                    </div>
                                )}
                            </div>

                            <DifficultyPills currentDifficulty={currentTableDifficulty} handleDifficultyChange={setCurrentTableDifficulty} />
                            <div className={styles.studentTableContainer}>
                                <StudentTable
                                    keyFilter={new Set(['difficulty', 'studentName', 'grade'])}
                                    style={{}}
                                    currentDifficulty={currentTableDifficulty}
                                    renderHeader={true}
                                />
                            </div>

                            <div className={styles.actions}>
                                {gradeBandsDiffer ? (
                                    <>
                                        <button
                                            className={styles.secondaryButton}
                                            onClick={() => handleAction(false)}
                                        >
                                            {isReassign ? 'Reassign & Keep Questions' : 'Keep Questions'}
                                        </button>
                                        <RegenerateQuestionsButton
                                            onClick={() => handleAction(true)}
                                        />
                                    </>
                                ) : (
                                    isReassign ? (
                                        <BlueButton
                                            label="Reassign"
                                            onClick={() => handleAction(false)}
                                        />
                                    ) : (
                                        <CopyLessonButton
                                            onClick={() => handleAction(false)}
                                        />
                                    )
                                )}
                            </div>
                        </ClassContextProvider>
                    ) : (
                        <>
                            <div className={styles.field}>
                                <label>Select Class</label>
                                <Dropdown
                                    options={availableClasses.map(c => c.name)}
                                    onSelect={handleClassSelect}
                                    placeholder="Select a class..."
                                    style={styles.wideDropdown}
                                    value={selectedClass?.name}
                                />
                            </div>
                            <div className={styles.noClassSelectedContainer}>
                                <img src={listView} alt="List View" />
                                Class roster for your selection will show up here
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
